import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import * as eventService from '@/api/services/event.service';
import type {
  CreateEventPhaseRequest,
  CreateEventRequest,
  EventFilters,
  UpdateEventPhaseRequest,
  UpdateEventRequest,
} from '@/api/types/event.types';

/** Query key constants for events. */
export const EVENT_KEYS = {
  all: ['events'] as const,
  lists: () => [...EVENT_KEYS.all, 'list'] as const,
  list: (filters?: EventFilters) => [...EVENT_KEYS.lists(), filters] as const,
  details: () => [...EVENT_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...EVENT_KEYS.details(), id] as const,
  phases: (eventId: string) => [...EVENT_KEYS.detail(eventId), 'phases'] as const,
};

/** Fetch paginated list of events with optional filters. */
export function useEvents(filters?: EventFilters) {
  return useQuery({
    queryKey: EVENT_KEYS.list(filters),
    queryFn: () => eventService.getEvents(filters),
  });
}

/** Fetch a single event by ID. */
export function useEvent(id: string) {
  return useQuery({
    queryKey: EVENT_KEYS.detail(id),
    queryFn: () => eventService.getEvent(id),
    enabled: !!id,
  });
}

/** Create a new event. Navigates to the detail page on success. */
export function useCreateEvent() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: CreateEventRequest) => eventService.createEvent(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.lists() });
      toast.success(t('events.createSuccess'));
      navigate(`/events/${data.id}`);
    },
    onError: () => {
      toast.error(t('events.createError'));
    },
  });
}

/** Update an existing event. */
export function useUpdateEvent(id: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: UpdateEventRequest) => eventService.updateEvent(id, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(EVENT_KEYS.detail(id), data);
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.lists() });
      toast.success(t('events.updateSuccess'));
    },
    onError: () => {
      toast.error(t('events.updateError'));
    },
  });
}

/** Delete an event. Navigates back to the list on success. */
export function useDeleteEvent() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => eventService.deleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.lists() });
      toast.success(t('events.deleteSuccess'));
      navigate('/events');
    },
    onError: () => {
      toast.error(t('events.deleteError'));
    },
  });
}

/** Fetch phases for a specific event. */
export function useEventPhases(eventId: string) {
  return useQuery({
    queryKey: EVENT_KEYS.phases(eventId),
    queryFn: () => eventService.getEventPhases(eventId),
    enabled: !!eventId,
  });
}

/** Create a new phase for an event. */
export function useCreateEventPhase(eventId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: CreateEventPhaseRequest) =>
      eventService.createEventPhase(eventId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.detail(eventId) });
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.phases(eventId) });
      toast.success(t('events.phaseCreated'));
    },
    onError: () => {
      toast.error(t('events.phaseCreateError'));
    },
  });
}

/** Update an existing event phase. */
export function useUpdateEventPhase(eventId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ phaseId, payload }: { phaseId: string; payload: UpdateEventPhaseRequest }) =>
      eventService.updateEventPhase(eventId, phaseId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.detail(eventId) });
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.phases(eventId) });
      toast.success(t('events.phaseUpdated'));
    },
    onError: () => {
      toast.error(t('events.phaseUpdateError'));
    },
  });
}

/** Delete an event phase. */
export function useDeleteEventPhase(eventId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (phaseId: string) =>
      eventService.deleteEventPhase(eventId, phaseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.detail(eventId) });
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.phases(eventId) });
      toast.success(t('events.phaseDeleted'));
    },
    onError: () => {
      toast.error(t('events.phaseDeleteError'));
    },
  });
}
